import { RefObject, useEffect, useRef } from "react";
import { Alert, Box, Button, CircularProgress, Paper, Stack, Typography } from "@mui/material";
import { packageFileOf, sectionOfId } from "./packageFiles";
import { EVIDENCE_IN_PROGRESS_STATUSES, EVIDENCE_PROGRESS_STAGES, EVIDENCE_STATUS_LABELS, EvidenceDraftStatus, type PackageProvider } from "../../types/evidence";

function ProgressRail({ status }: { status: EvidenceDraftStatus }) {
  const current = EVIDENCE_IN_PROGRESS_STATUSES.indexOf(status);
  return (
    <Stack direction="row" spacing={2} sx={{ justifyContent: "center", flexWrap: "wrap" }}>
      {EVIDENCE_IN_PROGRESS_STATUSES.map((step, index) => {
        const done = index < current;
        const active = index === current;
        const color = done ? "success.main" : active ? "primary.main" : "text.disabled";
        return (
          <Stack key={step} direction="row" spacing={0.75} sx={{ alignItems: "center" }}>
            <Box
              sx={{
                width: 8,
                height: 8,
                borderRadius: "50%",
                bgcolor: color,
                boxShadow: active ? "0 0 0 4px rgba(70, 95, 255, 0.18)" : "none",
              }}
            />
            <Typography variant="caption" sx={{ color, fontWeight: active ? 600 : 500 }}>
              {EVIDENCE_STATUS_LABELS[step]}
            </Typography>
          </Stack>
        );
      })}
    </Stack>
  );
}

// Stacked under the column the pane has no row height to fill, and the iframe measures nothing of
// its own, so without this the document renders at zero height and disappears on a laptop.
const STACKED_MIN_HEIGHT_PX = 480;
// A jumped-to section lands this far below the top, its outline whole.
const JUMP_MARGIN_PX = 16;
const FLASH_MS = 1200;
const OUTLINE_STYLE = "section[data-section]{border-radius:4px}"
  + ".cm-hover,section[data-section]:hover{outline:2px solid var(--cm-sw, #465fff);outline-offset:4px;border-radius:4px}"
  // As specific as the hover rule and after it, so a hovered flash keeps its 3px.
  + "section[data-section].cm-flash{outline:3px solid var(--cm-sw, #465fff);outline-offset:4px;border-radius:4px}";

// Each section outlines in its package file's swatch; with no provider the default blue stays.
function previewStyle(doc: Document, provider: PackageProvider | null): string {
  if (!provider) return OUTLINE_STYLE;
  const ids = new Set([...doc.querySelectorAll("section[data-section]")].map((section) => section.getAttribute("data-section") ?? ""));
  return OUTLINE_STYLE + [...ids].map((id) => {
    const found = sectionOfId(id);
    // The case header and the conclusion print in the facts file.
    const section = found === "case" || found === "conclusion" ? "factsOfTheCase" : found;
    return section ? `section[data-section="${id}"]{--cm-sw:${packageFileOf(provider, section, id).swatch}}` : "";
  }).join("");
}

export interface PreviewControls {
  /** Scrolls the preview to the first of the sections and flashes them all. */
  jumpTo: (sectionIds: string[]) => void;
  /** Outlines exactly the listed sections; an empty list clears the outline. */
  highlight: (sectionIds: string[]) => void;
}

export default function EvidencePreviewPane({ html, hasDraft, previewStale, previewExpired = false, previewError, onRetry, onDownloadPdf, inProgress, status, stacked, provider = null, controls, onSectionClick, onSectionHover }: {
  html: string | null;
  hasDraft: boolean;
  /** The document exists, but its stored snapshot predates this build and cannot be previewed. */
  previewStale: boolean;
  /** The document is old enough that the screenshots it printed have expired, so only its PDF shows it whole. */
  previewExpired?: boolean;
  /** The preview request itself failed, which is not the same as no document yet. */
  previewError?: string | null;
  onRetry?: () => void;
  onDownloadPdf?: () => void;
  inProgress?: boolean;
  status?: EvidenceDraftStatus;
  /** Below the stacking breakpoint the pane sits under the column instead of beside it. */
  stacked?: boolean;
  /** Receives the functions that let the section rows point at the preview. */
  controls?: RefObject<PreviewControls | null>;
  /** Colours each section's outline by the package file it lands in. */
  provider?: PackageProvider | null;
  /** A click inside a section of the preview, with that section's id. */
  onSectionClick?: (sectionId: string) => void;
  /** The section under the pointer in the preview, or null once it leaves them. */
  onSectionHover?: (sectionId: string | null) => void;
}) {
  const stage = inProgress && status ? EVIDENCE_PROGRESS_STAGES[status] : undefined;
  const frameRef = useRef<HTMLIFrameElement>(null);
  const sectionClickRef = useRef(onSectionClick);
  const sectionHoverRef = useRef(onSectionHover);
  const styleRef = useRef<HTMLStyleElement | null>(null);
  const unlistenRef = useRef<(() => void) | null>(null);
  const flashTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const clearFlash = () => {
    if (flashTimerRef.current) clearTimeout(flashTimerRef.current);
    flashTimerRef.current = null;
    try {
      frameRef.current?.contentDocument?.querySelectorAll(".cm-flash").forEach((el) => el.classList.remove("cm-flash"));
    } catch {
      // The document is gone, and its outlines with it.
    }
  };

  const unlisten = () => {
    try {
      unlistenRef.current?.();
    } catch {
      // The old document is gone, and its listener with it.
    }
    unlistenRef.current = null;
  };

  const onFrameLoad = () => {
    unlisten();
    try {
      const doc = frameRef.current?.contentDocument;
      if (!doc) return;
      const style = doc.createElement("style");
      style.textContent = previewStyle(doc, provider);
      doc.head?.appendChild(style);
      styleRef.current = style;
      const sectionId = (target: EventTarget | null) => (target as Element | null)?.closest?.("section[data-section]")?.getAttribute("data-section") ?? null;
      const onClick = (event: Event) => {
        clearFlash();
        const id = sectionId(event.target);
        if (id) sectionClickRef.current?.(id);
      };
      const onOver = (event: Event) => sectionHoverRef.current?.(sectionId(event.target));
      const onOut = (event: Event) => sectionHoverRef.current?.(sectionId((event as MouseEvent).relatedTarget));
      doc.addEventListener("click", onClick);
      doc.addEventListener("mouseover", onOver);
      doc.addEventListener("mouseout", onOut);
      unlistenRef.current = () => {
        doc.removeEventListener("click", onClick);
        doc.removeEventListener("mouseover", onOver);
        doc.removeEventListener("mouseout", onOut);
      };
    } catch {
      // The document is unavailable; nothing to listen to.
    }
  };

  const jump = (sectionIds: string[]) => {
    try {
      const doc = frameRef.current?.contentDocument;
      const sections = sectionIds.flatMap((id) => [...(doc?.querySelectorAll(`section[data-section="${id}"]`) ?? [])]);
      if (!doc || sections.length === 0) return;
      clearFlash();
      sections.forEach((section) => section.classList.add("cm-flash"));
      flashTimerRef.current = setTimeout(clearFlash, FLASH_MS);
      // Not scrollIntoView: from a same-origin frame that also scrolls the page around it.
      const top = sections[0].getBoundingClientRect().top + doc.documentElement.scrollTop - JUMP_MARGIN_PX;
      (doc.scrollingElement ?? doc.documentElement).scrollTo?.({ top: Math.max(top, 0), behavior: "smooth" });
    } catch {
      // The document is unavailable; the click does nothing.
    }
  };

  const highlight = (sectionIds: string[]) => {
    try {
      frameRef.current?.contentDocument?.querySelectorAll("section[data-section]").forEach((section) => {
        section.classList.toggle("cm-hover", sectionIds.includes(section.getAttribute("data-section") ?? ""));
      });
    } catch {
      // The document is unavailable; nothing to outline.
    }
  };

  useEffect(() => {
    sectionClickRef.current = onSectionClick;
    sectionHoverRef.current = onSectionHover;
    if (!controls) return;
    controls.current = { jumpTo: jump, highlight };
    return () => { controls.current = null; };
  });

  useEffect(() => () => { unlisten(); clearFlash(); }, []);

  // The provider can land after the document did.
  useEffect(() => {
    try {
      const style = styleRef.current;
      if (style?.ownerDocument) style.textContent = previewStyle(style.ownerDocument, provider);
    } catch {
      // The document is gone; the next load styles the new one.
    }
  }, [provider]);

  return (
    <Paper
      variant="outlined"
      aria-busy={!!stage}
      sx={{
        position: "relative",
        flexGrow: 1,
        display: "flex",
        flexDirection: "column",
        overflow: "hidden",
        bgcolor: "grey.100",
        ...(stacked && { minHeight: STACKED_MIN_HEIGHT_PX }),
      }}
    >
      {html ? (
        // srcDoc, not src: an iframe request carries no Authorization header. The sandbox keeps the
        // static dossier scriptless; the same origin lets the page style the document and listen for clicks and hover.
        <iframe
          ref={frameRef}
          title="Evidence preview"
          srcDoc={html}
          sandbox="allow-same-origin"
          onLoad={onFrameLoad}
          // The frame's own document often misses the mouseout when the pointer leaves the frame.
          onMouseLeave={() => sectionHoverRef.current?.(null)}
          style={{ display: "block", width: "100%", flex: 1, border: "none" }}
        />
      ) : stage ? (
        <Stack spacing={1.5} sx={{ p: 4 }} aria-hidden>
          {[1, 0.85, 0.6, 0.9, 0.45].map((width, index) => (
            <Box key={index} sx={{ height: 12, width: `${width * 100}%`, borderRadius: 1, bgcolor: "grey.300" }} />
          ))}
        </Stack>
      ) : previewExpired ? (
        <Box sx={{ p: 3 }}>
          <Alert
            severity="info"
            action={onDownloadPdf && <Button color="inherit" size="small" onClick={onDownloadPdf}>Download PDF</Button>}
          >
            The live preview has expired. Download the PDF to see the document, or regenerate the evidence to refresh the preview.
          </Alert>
        </Box>
      ) : previewError ? (
        <Box sx={{ p: 3 }}>
          <Alert
            severity="error"
            action={onRetry && <Button color="inherit" size="small" onClick={onRetry}>Retry</Button>}
          >
            {previewError}
          </Alert>
        </Box>
      ) : (
        <Box sx={{ p: 3 }}>
          <Typography variant="body2" color="text.secondary">
            {!hasDraft
              ? "No evidence draft yet."
              : previewStale
                ? "This preview is out of date. Run Update evidence to refresh it."
                : "The preview appears once the document has been generated."}
          </Typography>
        </Box>
      )}

      {stage && status && (
        <Box
          sx={{
            position: "absolute",
            inset: 0,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            p: 2,
            bgcolor: "rgba(255, 255, 255, 0.72)",
            backdropFilter: "blur(3px)",
          }}
        >
          <Stack
            spacing={2}
            sx={{
              alignItems: "center",
              textAlign: "center",
              maxWidth: 420,
              px: 4,
              py: 3.5,
              borderRadius: 2,
              bgcolor: "background.paper",
              border: "1px solid",
              borderColor: "divider",
              boxShadow: 3,
            }}
          >
            <CircularProgress size={56} thickness={3.6} sx={{ color: "primary.main" }} />
            <Box>
              <Typography variant="subtitle1" aria-live="polite" sx={{ fontWeight: 600 }}>
                {stage.title}
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                {stage.subtitle}
              </Typography>
            </Box>
            <ProgressRail status={status} />
            <Typography variant="caption" color="text.secondary">
              You can leave this page. The draft keeps generating and the preview loads when it is ready.
            </Typography>
          </Stack>
        </Box>
      )}
    </Paper>
  );
}
